/**
 * Probe curated help targets: visibility + why a highlight might fail.
 *
 * Snapshot payload is ids + path only — never DOM text / PHI.
 */

import {
  HELP_TARGET_SELECTORS,
  isHelpTargetId,
  resolveHelpTargetElement,
  type HelpTargetId,
} from './helpTargets';

/** Parent targets that must usually be open/present before a child exists in DOM. */
export const HELP_TARGET_REQUIRES: Partial<
  Record<HelpTargetId, readonly HelpTargetId[]>
> = {
  'nav.settings': ['nav.profile'],
  'nav.team': ['nav.profile'],
  'session.style-brief': ['session.style-row'],
  'session.style-detailed': ['session.style-row'],
  'session.style-super-detailed': ['session.style-row'],
  'session.regenerate': ['session.more-options'],
  'session.create-document': ['session.create'],
  'session.create-letter': ['session.create'],
};

export type HighlightMissReason =
  | 'unknown_target'
  | 'not_in_dom'
  | 'needs_open'
  | 'parent_missing';

export type HelpTargetProbe = {
  target: string;
  present: boolean;
  reason: HighlightMissReason | null;
  /** Parents that should be opened / present first. */
  requires: HelpTargetId[];
  /** First present parent we can fall back to highlighting. */
  fallbackTarget: HelpTargetId | null;
  /** Human-readable, safe for callout + agent context. */
  explanation: string;
};

export type UiHelpSnapshot = {
  path: string;
  /** Curated targets currently in the DOM (allowlist ids only). */
  visible: HelpTargetId[];
};

export const listVisibleHelpTargets = (): HelpTargetId[] => {
  const visible: HelpTargetId[] = [];
  for (const id of Object.keys(HELP_TARGET_SELECTORS) as HelpTargetId[]) {
    if (resolveHelpTargetElement(id)) visible.push(id);
  }
  return visible;
};

export const buildUiHelpSnapshot = (): UiHelpSnapshot => ({
  path: typeof window !== 'undefined' ? window.location.pathname : '',
  visible: listVisibleHelpTargets(),
});

export const probeHelpTarget = (target: string): HelpTargetProbe => {
  if (!isHelpTargetId(target)) {
    return {
      target,
      present: false,
      reason: 'unknown_target',
      requires: [],
      fallbackTarget: null,
      explanation: `Unknown help target "${target}" — not in the curated allowlist.`,
    };
  }

  if (resolveHelpTargetElement(target)) {
    return {
      target,
      present: true,
      reason: null,
      requires: [...(HELP_TARGET_REQUIRES[target] ?? [])],
      fallbackTarget: null,
      explanation: '',
    };
  }

  const requires = [...(HELP_TARGET_REQUIRES[target] ?? [])];
  const missingParents = requires.filter((id) => !resolveHelpTargetElement(id));
  const presentParents = requires.filter((id) => resolveHelpTargetElement(id));
  const fallbackTarget = presentParents[0] ?? null;

  if (requires.length > 0 && missingParents.length === 0) {
    return {
      target,
      present: false,
      reason: 'needs_open',
      requires,
      fallbackTarget,
      explanation:
        `${target} is not on screen yet — open ${requires.join(' → ')} first ` +
        `(it is a menu/option that only mounts after that control is opened).`,
    };
  }

  if (requires.length > 0 && missingParents.length > 0) {
    return {
      target,
      present: false,
      reason: 'parent_missing',
      requires,
      fallbackTarget: null,
      explanation:
        `${target} cannot appear yet — missing parent control(s) ` +
        `${missingParents.join(', ')}. Navigate to the matching screen first.`,
    };
  }

  return {
    target,
    present: false,
    reason: 'not_in_dom',
    requires,
    fallbackTarget: null,
    explanation:
      `${target} is not in the DOM on this screen ` +
      `(path ${typeof window !== 'undefined' ? window.location.pathname : '?'}). ` +
      'Open the matching page or panel, then try again.',
  };
};
